import { createContext, useContext, useState, useEffect } from 'react'
import LogoSvg from '../assets/images/svg/logo.svg'
import InstructionModal from '../components/modais/instruction_modal'
import InstructionsIcon from '../assets/images/info.png'
import { IHeaderProps, initialHeaderConfig } from '../components/header_home'
import { useGame } from '.'
import { addLocalStorage } from '../utils/storage_helpers'
import FileModal from '../components/modais/file_modal'

type IPageContextProps = {
  headerType: 'title' | 'timer' | 'blank';
  showTooltip: boolean|undefined|null;
  background: string|undefined|null;
  logo: string;
  baseColor: string;
  soundActivated: boolean;
  headerConfig: IHeaderProps;
  gameDocument: string;
  hasBackButton: boolean;
  setHasBackButton: (state: boolean) => void;
  toggleTooltip: (value?: boolean|undefined|null) => void;
  toggleBackground: (source?: string|null) => void;
  toggleLogo: (source?: string|null) => void;
  toggleHeader: (type: 'title'|'timer'|'blank') => void;
  toggleBaseColor: (color: string) => void;
  toggleGameDocument: (value: string) => void;
  toggleDocument: () => void;
  toggleGeneralSound: () => void;
  toggleInstructions: () => void;
  toggleInstructionsContent: (image: string, content: string) => void;
  toggleHeaderConfig: (props: any) => void;
}

type InstructionsContextProps = {
  imageUrl: string,
  content: string
}|null

const PageContext = createContext<IPageContextProps>({} as IPageContextProps)

export const PageProvider = ({ children }: any) => {
  const [showTooltip, setShowTooltip] = useState<boolean|undefined|null>(null)
  const [background, setBackground] = useState<string|null>()
  const [logo, setLogo] = useState<string>(LogoSvg)
  const [soundActivated, setSoundActivated] = useState<boolean>(true)
  const [headerType, setHeaderType] = useState<'title' | 'timer' | 'blank'>('title')
  const [baseColor, setBaseColor] = useState<string>('#000000')
  const [gameInstructions, setGameInstructions] = useState<InstructionsContextProps>(null)
  const [showInstructions, setShowInstructions] = useState<boolean>(false)
  const [headerConfig, setHeaderConfig] = useState<IHeaderProps>(initialHeaderConfig)
  const [gameDocument, setGameDocument] = useState<string>('')
  const [showDocument, setShowDocument] = useState<boolean>(false)
  const [hasBackButton, setHasBackButton] = useState(false)

  const { game } = useGame()

  const toggleGameDocument = (value: string) => {
    setGameDocument(value)
  }

  const toggleGeneralSound = () => {
    setSoundActivated(prev => !prev)
    addLocalStorage('soundIsActivated', JSON.stringify(!soundActivated))
  }

  const toggleInstructions = () => {
    setShowInstructions(prev => !prev)
  }

  const toggleDocument = () => {
    setShowDocument(prev => !prev)
  }

  const toggleTooltip = (value?: boolean|undefined|null) => {
    setShowTooltip(value)
  }

  const toggleBackground = (source?: string|null) => {
    if (!source) {
      setBackground(null)
      return
    }
    setBackground(source)
  }

  const toggleLogo = (source?: string|null) => {
    if (!source) {
      setLogo(LogoSvg)
      return
    }
    setLogo(source)
  }

  const toggleHeader = (type: 'title' | 'timer' | 'blank') => {
    setHeaderType(type)
  }

  const toggleBaseColor = (color: string) => {
    setBaseColor(color)
  }

  const toggleInstructionsContent = (image: string, content: string) => {
    if (content) {
      setGameInstructions({
        imageUrl: image,
        content: content
      })
    } else {
      setGameInstructions(null)
    }
  }

  const toggleHeaderConfig = (props: any) => {
    const concatConfig = { ...headerConfig, ...props }
    setHeaderConfig(concatConfig)
  }

  useEffect(() => {
    addLocalStorage('soundIsActivated', JSON.stringify(true))
    if (game?.baseColor) {
      toggleBaseColor(game.baseColor)
    }
  }, [game])

  return (
    <PageContext.Provider
        value={{
          hasBackButton,
          gameDocument,
          headerType,
          headerConfig,
          logo,
          toggleLogo,
          showTooltip,
          background,
          soundActivated,
          toggleTooltip,
          toggleBackground,
          toggleHeader,
          baseColor,
          toggleBaseColor,
          toggleGeneralSound,
          toggleInstructions,
          toggleInstructionsContent,
          toggleHeaderConfig,
          toggleGameDocument,
          toggleDocument,
          setHasBackButton
        }}>
      {!!gameInstructions && showInstructions &&
        <InstructionModal
          content={gameInstructions?.content || ''}
          url={gameInstructions?.imageUrl || InstructionsIcon}
          onAction={toggleInstructions}
        />
      }

      {!!gameDocument && showDocument &&
        <FileModal
          filePath={gameDocument}
          handleClose={() => toggleDocument()}
        />
      }
      {children}
    </PageContext.Provider>
  )
}

export const usePage = () => {
  const context = useContext(PageContext)
  if (!context) throw new Error("It's necessary to use the PageProvider")

  return (
    { ...context }
  )
}
